import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/ui";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import UrgentCare from "./pages/UrgentCare";
import CliniciansPrimaryCare from "./pages/CliniciansPrimaryCare";
import CliniciansUrgentCare from "./pages/CliniciansUrgentCare";
import Patients from "./pages/Patients";
import Providers from "./pages/Providers";
import Integration from "./pages/Integration";
import Contact from "./pages/Contact";
import ContactThankYou from "./pages/ContactThankYou";
import AboutUs from "./pages/AboutUs";
import OurVision from "./pages/OurVision";
import News from "./pages/News";
import Careers from "./pages/Careers";
import JobApplication from "./pages/JobApplication";
import BookDemo from "./pages/BookDemo";
import DemoThankYou from "./pages/DemoThankYou";
import ApplicationThankYou from "./pages/ApplicationThankYou";
import DynamicCareerRoutes from "./components/DynamicCareerRoutes";
import Blogs from "./pages/Blogs";
import BlogArticle from "./pages/BlogArticle";
import Resources from "./pages/Resources";
import Clinical from "./pages/solutions/Clinical";
import OccupationalMedicine from "./pages/solutions/OccupationalMedicine";
import PracticeManagement from "./pages/solutions/PracticeManagement";
import RevenueCycleManagement from "./pages/solutions/RevenueCycleManagement";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import HIPAACompliance from "./pages/HIPAACompliance";
import Security from "./pages/Security";
import SMSTerms from "./pages/SMSTerms";
import NotFound from "./pages/not-found";
import { AdminAuthProvider } from "./contexts/AdminAuthContext";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";

/** Admin routes — no website Header/Footer, separate layout */
function AdminRouter() {
  return (
    <AdminAuthProvider>
      <Switch>
        <Route path="/admin/login" component={AdminLogin} />
        <Route path="/admin" component={AdminDashboard} />
        <Route path="/admin/:rest*" component={AdminDashboard} />
      </Switch>
    </AdminAuthProvider>
  );
}

/** Public website routes — with Header & Footer */
function WebsiteRouter() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Match ucaremr-website: body scroll with fixed header */}
      <main className="pt-16">
        <Switch>
        <Route path="/" component={Home} />
        <Route path="/solutions/clinical" component={Clinical} />
        <Route path="/solutions/occupational-medicine" component={OccupationalMedicine} />
        <Route path="/solutions/practice-management" component={PracticeManagement} />
        <Route path="/solutions/revenue-cycle-management" component={RevenueCycleManagement} />
        <Route path="/platform" component={UrgentCare} />
        <Route path="/platform/urgent-care" component={UrgentCare} />
        <Route path="/clinicians" component={CliniciansPrimaryCare} />
        <Route path="/clinicians/primary-care" component={CliniciansPrimaryCare} />
        <Route path="/clinicians/urgent-care" component={CliniciansUrgentCare} />
        <Route path="/patients" component={Patients} />
        <Route path="/providers" component={Providers} />
        <Route path="/integration" component={Integration} />
        <Route path="/contact" component={Contact} />
        <Route path="/contact/thank-you" component={ContactThankYou} />
        <Route path="/about-us" component={AboutUs} />
        <Route path="/our-vision" component={OurVision} />
        <Route path="/news" component={News} />
        <Route path="/careers" component={Careers} />
        <Route path="/careers/apply" component={JobApplication} />
        <Route path="/careers/apply/:slug" component={JobApplication} />

        <Route path="/book-demo" component={BookDemo} />
        <Route path="/demo/thank-you" component={DemoThankYou} />
        <Route path="/application/thank-you" component={ApplicationThankYou} />

        <Route path="/blogs" component={Blogs} />
        <Route path="/blog/:slug" component={BlogArticle} />
        <Route path="/resources" component={Resources} />

        <Route path="/privacy-policy" component={PrivacyPolicy} />
        <Route path="/terms-of-service" component={TermsOfService} />
        <Route path="/hipaa-compliance" component={HIPAACompliance} />
        <Route path="/security" component={Security} />
        <Route path="/sms-terms" component={SMSTerms} />

        {/* Dynamic career routes based on database - must be near the end before NotFound */}
        <DynamicCareerRoutes />

        <Route component={NotFound} />
        </Switch>
      </main>
      <Footer />
    </div>
  );
}

function Router() {
  const [location] = useLocation();

  // Admin routes get their own layout (no website Header/Footer)
  if (location.startsWith('/admin')) {
    return <AdminRouter />;
  }

  return <WebsiteRouter />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <Router />
    </QueryClientProvider>
  );
}

export default App;
